// A NYUGTA-REKORD alakja — kanonikus forrás (CLAUDE.md 10. szabály).
// Terv: ClickUp 123qmzqzk1r §6.2.
//
// MIÉRT KÜLÖN MODUL, ÉS MIÉRT MOST: a nyugtát KÉT helyről írjuk — a jogi
// kapu modáljából és (a következő körben) a REGISZTRÁCIÓBÓL (§7.3/d: aki
// most fogadja el a feltételeket, ne kapjon rá blokkoló ablakot az
// onboarding után). Ha a két hely külön állítaná össze a doksi-ID-t vagy a
// payloadot, egy mezőnyi eltérés is azt jelentené, hogy a kapu nem találja
// meg a saját nyugtáját — és a user beragadna.
//
// ⚠️ A `acceptedAt`-ot SZÁNDÉKOSAN nem ez a modul állítja be: a rules
// `acceptedAt == request.time`-ot követel, tehát csak szerver-timestamp
// mehet. A hívó adja be (`serverTimestamp()`), hogy ez a modul tiszta és
// tesztelhető maradjon.

import type { LegalDocType, LegalGateKind } from "./legalGate";

/** `legalAcks/{uid}_{docType}_{version}` — DETERMINISZTIKUS doksi-ID.
 *
 * Miért determinisztikus (§6.2): idempotens (kétszer megnyomott gomb nem
 * csinál két rekordot) · nincs hozzá query, tehát nincs index sem (1
 * config-olvasás + N getDoc) · és a rules `update`/`delete` tiltása mellett
 * egy már létező nyugtát nem lehet felülírni.
 */
export function ackDocId(uid: string, docType: string, version: string): string {
  return `${uid}_${docType}_${version}`;
}

export interface AckPayloadInput {
  uid: string;
  docType: LegalDocType;
  version: string;
  gate: LegalGateKind;
  /** "web" | "native" — a platform-kapu `IS_NATIVE`-jából. */
  surface: "web" | "native";
  /** Szerver-timestamp sentinel; a rules ezt követeli meg. */
  acceptedAt: unknown;
  /** A futó build commit-hash-e (`buildInfo`), ha ismert. */
  appVersion?: string | null;
}

/**
 * A `legalAcks` dokumentum tartalma.
 *
 * ⚠️ A mezőlista a `firestore.rules` `validAck()` `hasOnly`/`hasAll`
 * listájával EGYEZIK, és ez nem véletlen egyezés: egy ismeretlen mező a
 * rules-ban ELUTASÍTÁST ad, nem figyelmeztetést. Ha itt új mező kerül be,
 * a rules-t is deployolni kell — és a Store-os kliens miatt minden
 * rules-deploy költség (ld. docs/UZEMELTETES.md 7. szakasz).
 *
 * Az `appVersion` SZÁNDÉKOSAN kimarad a dokumentumból, ha nincs értéke —
 * nem `null`-ként kerül be. Indok: a rules `hasOnly` listája megengedi, de
 * egy `null` mező azt sugallná, hogy „mértük, és nem volt" — holott az
 * jelenti, hogy „nem tudtuk megmérni".
 */
export function buildAckPayload(input: AckPayloadInput): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    userId: input.uid,
    docType: input.docType,
    version: input.version,
    acceptedAt: input.acceptedAt,
    surface: input.surface,
    gate: input.gate,
  };
  const v = (input.appVersion || "").trim();
  if (v) payload.appVersion = v;
  return payload;
}

/** A rules `validAck()` mezőlistája — a teszt ezzel veti össze a payloadot. */
export const ACK_ALLOWED_FIELDS = [
  "userId", "docType", "version", "acceptedAt", "surface", "gate", "appVersion",
] as const;

export const ACK_REQUIRED_FIELDS = [
  "userId", "docType", "version", "acceptedAt", "surface", "gate",
] as const;
